export type BundleSchedule = 'monthly' | 'annual-upfront' | 'apm' | 'other';

// Plain store shapes so the quote store can share this check without a cycle through the cart store.
export interface AioBundleRetentionStores {
  once: {
    plan: number;
    isAioBundle: boolean;
    getFeatureFlag: (flag: string) => boolean;
  };
  cart: { plan: number };
  checkout: {
    isSubscriptionStep: boolean;
    isAioBundleFlow: boolean;
    suppressBundleRetention: boolean;
  };
  plans: {
    isMonthly: (planId: number) => boolean;
    isAnnualUpfront: (planId: number) => boolean;
    isAnnualPaidMonthly: (planId: number) => boolean;
    isNewPlan: (planId: number) => boolean;
  };
}

export function bundleScheduleOf(
  plans: AioBundleRetentionStores['plans'],
  planId: number
): BundleSchedule {
  if (plans.isMonthly(planId)) {
    return 'monthly';
  }
  if (plans.isAnnualUpfront(planId)) {
    return 'annual-upfront';
  }
  if (plans.isAnnualPaidMonthly(planId)) {
    return 'apm';
  }
  return 'other';
}

export function isAioBundleCustomer(
  once: AioBundleRetentionStores['once']
): boolean {
  return Boolean(
    once.getFeatureFlag('wo-3223-enable-aio-bundle') && once.isAioBundle
  );
}

// Plan step only (on the add-on step cart.plan mirrors once.plan); the bundle checkout and "Switch to annual" handle their own pricing.
export function isInBundleChangePlanCheckout({
  once,
  checkout,
}: AioBundleRetentionStores): boolean {
  return (
    isAioBundleCustomer(once) &&
    checkout.isSubscriptionStep &&
    !checkout.isAioBundleFlow &&
    !checkout.suppressBundleRetention
  );
}

// The bundle only exists on new tiers and survives only on the same schedule.
export function isAioBundleRetained(stores: AioBundleRetentionStores): boolean {
  if (!isInBundleChangePlanCheckout(stores)) {
    return false;
  }
  const { once, cart, plans } = stores;
  if (!plans.isNewPlan(cart.plan) || !plans.isNewPlan(once.plan)) {
    return false;
  }
  const cartSchedule = bundleScheduleOf(plans, cart.plan);
  return (
    cartSchedule !== 'other' &&
    cartSchedule === bundleScheduleOf(plans, once.plan)
  );
}
